import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, useRef, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { Toaster } from "@/components/ui/sonner";
import { useCartSync } from "@/hooks/useCartSync";
import { Analytics } from "@/components/analytics";

function NotFoundComponent() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const path = window.location.pathname + window.location.search;
    const referrer = document.referrer || "(direct)";
    const w = window as unknown as { dataLayer?: unknown[] };
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({ event: "page_not_found", page_path: path, referrer });
    console.warn("[404]", path, "from", referrer);
  }, []);
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      {/* React 19 hoists these into <head> for soft-404 noindex. */}
      <title>Page not found — House of Apocrypha</title>
      <meta name="robots" content="noindex,nofollow" />
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const msg = (error as { message?: string })?.message ?? String(error);
  const name = (error as { name?: string })?.name ?? "";
  return (
    /Importing a module script failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /Load failed/i.test(msg) ||
    /Unable to preload CSS/i.test(msg) ||
    /Failed to load module script/i.test(msg) ||
    /NetworkError when attempting to fetch resource/i.test(msg) ||
    /ChunkLoadError/i.test(name) ||
    /ChunkLoadError/i.test(msg)
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  const chunkError = isChunkLoadError(error);
  const retriesRef = useRef(0);

  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  // Auto-recover from transient chunk/module import failures (common during client-side navigation).
  useEffect(() => {
    if (!chunkError || retriesRef.current >= 2) return;
    retriesRef.current += 1;
    const t = setTimeout(() => {
      router.invalidate();
      reset();
    }, retriesRef.current === 1 ? 80 : 400);
    return () => clearTimeout(t);
  }, [chunkError, error, router, reset]);

  if (chunkError) {
    // Stay quiet while auto-retrying — avoid the jarring error flash.
    return <div className="min-h-screen bg-background" aria-hidden />;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "House of Apocrypha — Natural Perfumes & Ritual" },
      {
        name: "description",
        content:
          "House of Apocrypha crafts natural perfumes rooted in story, ritual and alchemy — slow-made fragrances composed from rare botanicals.",
      },
      { name: "author", content: "House of Apocrypha" },
      // Google Search Console domain verification
      { name: "google-site-verification", content: "eYonBsl0XYFDEbRbxqW1L7Atu4GUHERKBcSV8RBGAAE" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "House of Apocrypha" },
      { property: "og:url", content: "https://houseofapocrypha.com/" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@houseofapocrypha" },
    ],
    links: [
      { rel: "icon", type: "image/png", sizes: "any", href: "/favicon.png?v=3" },
      { rel: "shortcut icon", type: "image/png", href: "/favicon.png?v=3" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png?v=3" },
      { rel: "manifest", href: "/manifest.webmanifest?v=3" },
      // Above-the-fold faces only, matched to the weights actually rendered:
      // logo = Libre Caslon 400, body = Cormorant 600, eyebrow/buttons = Cinzel 500,
      // headings = Cinzel 600. Everything else loads normally via CSS.
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "/fonts/libre-caslon-display-latin-400-normal.woff2",
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "/fonts/cormorant-garamond-latin-600-normal.woff2",
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "/fonts/cinzel-latin-500-normal.woff2",
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "/fonts/cinzel-latin-600-normal.woff2",
        crossOrigin: "anonymous",
      },
      {
        rel: "stylesheet",
        href: appCss,
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "House of Apocrypha",
          url: "https://houseofapocrypha.com/",
          description: "Natural perfumes rooted in story, ritual and alchemy.",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "House of Apocrypha",
          url: "https://houseofapocrypha.com/",
        }),
      },
      // Shopify Shop JS — registers <shop-pay-button> and <shop-pay-installments-message>
      { src: "https://cdn.shopify.com/shopifycloud/shop-js/client.js", async: true, defer: true },
      // Analytics (GTM + GA4) load after first paint — see <Analytics /> below.
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        <noscript>
          <iframe
            src="https://www.googletagmanager.com/ns.html?id=GTM-KXQLR2WX"
            height={0}
            width={0}
            style={{ display: "none", visibility: "hidden" }}
            title="Google Tag Manager"
          />
        </noscript>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <CartSyncMount />
      <Analytics />
      <Outlet />
      <Toaster position="top-center" theme="dark" />
    </QueryClientProvider>
  );
}

function CartSyncMount() {
  useCartSync();
  return null;
}
